import { checkIfStartTime } from '@/lib/commonHelpers';
import {
  canStudentReview,
  isDistrictQBAuthorWithMonitorFeature,
  shouldShowSessions,
} from '@/pages/experiences/utils/experienceHelperMethods';
import type { ExperienceAccessContextProps } from './types';

/**
 * Simplified experience permissions calculator
 *
 * This function directly calculates each permission state without complex intermediate layers.
 * Each permission check is self-contained and easy to understand.
 */
export function calculateExperiencePermissions(context: ExperienceAccessContextProps) {
  const { embedded, experience, enabledFeatures, currentUser } = context;

  const experienceSettings = experience.settings;
  const experienceRubric = experience.rubric;
  // Extract commonly used values
  const userRole = currentUser?.role ?? 'anonymous';
  const isQuestionBankExperience = !!experience?.questionbank;
  const isPaperMode = experienceSettings?.mode === 'paper';
  const isDraft = experience?.status === 'draft';
  const hasNoSubmissions = experience?.submissions_count === 0;
  const isDistrictQBAuthor =
    currentUser?.org?.org_type === 'district' && currentUser?.role === 'qb_author';
  const isValidRubricType = experienceRubric?.type_c !== 'comments_only';
  const hasAnalyticsFeature =
    enabledFeatures?.experience_analytics ||
    import.meta.env.VITE_MODE === 'development' ||
    import.meta.env.VITE_MODE === 'staging';

  return {
    create: checkCanCreate(context, userRole, isQuestionBankExperience, embedded || false),
    deliver: checkCanDeliver(context, userRole),
    monitor: checkCanMonitor(context, userRole, isPaperMode),
    grade: checkCanGrade(context, userRole, isDraft),
    analytics: checkCanAnalytics(
      context,
      userRole,
      hasNoSubmissions,
      hasAnalyticsFeature,
      isValidRubricType,
      isDistrictQBAuthor
    ),
  };
}

/**
 * Check Create/View permissions
 */
function checkCanCreate(
  context: ExperienceAccessContextProps,
  userRole: string,
  isQuestionBankExperience: boolean,
  embedded: boolean
) {
  const { experience, currentUser, enabledFeatures } = context;
  const isSessionsEnabled = shouldShowSessions(enabledFeatures, experience);

  // Question bank specific logic
  if (isQuestionBankExperience) {
    if (
      isSessionsEnabled &&
      (userRole === 'teacher' || userRole === 'admin') &&
      !isInvigilatorOnly(experience, currentUser)
    ) {
      return {
        disabled: false,
        hidden: false,
        reason: 'ExperiencePermissions.common_assessment_cannot_create',
        label: 'CommonText.view',
      };
    }
    const isQBAuthor = userRole === 'qb_author';
    const allowedQbIds = currentUser?.custom_fields?.allowed_questionbank_ids ?? [];
    const hasQBAccess = isQBAuthor && allowedQbIds.includes(experience?.questionbank?.id);
    if (
      isQBAuthor &&
      currentUser?.org?.org_type === 'district' &&
      experience?.org_id === currentUser.org_id
    ) {
      return {
        disabled: false,
        hidden: false,
        reason: '',
        label: 'ExperiencePageHeader.create_tab',
      };
    }

    if (!hasQBAccess) {
      return {
        disabled: true,
        hidden: false,
        reason: 'ExperiencePermissions.no_permission_edit_questionbank',
        label: 'CommonText.view',
      };
    }
  }

  // Role-based permissions
  const canEdit = canUserEdit(context);
  const canView = canUserView(context);

  if (!canView && !canEdit) {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.no_permission_view_edit',
      label: 'CommonText.view',
    };
  }

  return {
    disabled: false,
    hidden: false,
    reason: '',
    label: embedded || !canEdit ? 'CommonText.view' : 'ExperiencePageHeader.create_tab',
  };
}

/**
 * Check Deliver permissions
 */
function checkCanDeliver(context: ExperienceAccessContextProps, userRole: string) {
  const { embedded, experience, currentUser, enabledFeatures } = context;
  const isSessionsEnabled = shouldShowSessions(enabledFeatures, experience);
  // Disable deliver tab in embedded mode
  if (embedded) {
    return {
      disabled: true,
      hidden: true,
      reason: 'ExperiencePermissions.deliver_disabled_embedded_mode',
      label: 'ExperiencePageHeader.deliver_tab',
    };
  }

  if (userRole === 'student' || userRole === 'parent') {
    return {
      disabled: true,
      hidden: false,
      reason:
        userRole === 'student'
          ? 'ExperiencePermissions.students_cannot_deliver'
          : 'ExperiencePermissions.parents_cannot_deliver',
      label: 'ExperiencePageHeader.deliver_tab',
    };
  }

  // Common assessment - enable deliver tab for teachers/admins
  if (
    isSessionsEnabled &&
    (userRole === 'teacher' || userRole === 'admin') &&
    !isInvigilatorOnly(experience, currentUser)
  ) {
    return {
      disabled: false,
      hidden: false,
      reason: '',
      label: 'ExperiencePageHeader.deliver_tab',
    };
  }

  if (
    currentUser.role === 'qb_author' &&
    currentUser?.org?.org_type === 'district' &&
    experience?.org_id === currentUser.org_id
  ) {
    return {
      disabled: false,
      hidden: false,
      reason: '',
      label: 'ExperiencePageHeader.deliver_tab',
    };
  }

  const canDeliver = canUserDeliver(context);

  if (!canDeliver && !isOwnOrgExperience(experience, currentUser)) {
    return {
      disabled: false,
      hidden: true,
      reason: 'ExperiencePermissions.you_dont_have_access_to_deliver_this_assessment',
      label: 'ExperiencePageHeader.deliver_tab',
    };
  }

  return {
    disabled: !canDeliver,
    hidden: false,
    reason: canDeliver ? '' : 'ExperiencePermissions.no_permission_deliver',
    label: 'ExperiencePageHeader.deliver_tab',
  };
}

/**
 * Check Monitor permissions
 */
function checkCanMonitor(
  context: ExperienceAccessContextProps,
  userRole: string,
  isPaperMode: boolean
) {
  const { embedded, enabledFeatures, currentUser, experience } = context;
  const isSessionsEnabled = shouldShowSessions(enabledFeatures, experience);
  const isDistrictQBAuthorWithMonitorFlag = isDistrictQBAuthorWithMonitorFeature(
    enabledFeatures,
    currentUser
  );

  // Disable monitor tab in embedded mode
  if (embedded) {
    return {
      disabled: true,
      hidden: true,
      reason: 'ExperiencePermissions.monitor_disabled_embedded_mode',
      label: 'ExperiencePageHeader.monitor_tab',
    };
  }

  // Sessions decide mode per session, so the experience-level paper mode must not
  // disable monitoring for a sessions-enabled experience.
  if (isPaperMode && !isSessionsEnabled && !isDistrictQBAuthorWithMonitorFlag) {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.monitoring_disabled_paper_mode',
      label: 'ExperiencePageHeader.monitor_tab',
    };
  }

  if (userRole === 'student') {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.students_cannot_monitor',
      label: 'ExperiencePageHeader.monitor_tab',
    };
  }

  // Common assessment - enable monitor tab for teachers/admins
  if (
    isSessionsEnabled &&
    (userRole === 'teacher' || userRole === 'admin') &&
    !isInvigilatorOnly(experience, currentUser)
  ) {
    return {
      disabled: false,
      hidden: false,
      reason: '',
      label: 'ExperiencePageHeader.monitor_tab',
    };
  }

  const canMonitor = canUserMonitor(context);

  if (!canMonitor && !isOwnOrgExperience(experience, currentUser)) {
    return {
      disabled: false,
      hidden: true,
      reason: 'ExperiencePermissions.you_dont_have_access_to_monitor_this_assessment',
      label: 'ExperiencePageHeader.monitor_tab',
    };
  }

  return {
    disabled: !canMonitor,
    hidden: false,
    reason: canMonitor ? '' : 'ExperiencePermissions.no_permission_monitor',
    label: 'ExperiencePageHeader.monitor_tab',
  };
}

function getIsDupsEditable(experience: any): boolean {
  const experienceQuestionbank = experience?.questionbank;
  const questionbankSettings = experienceQuestionbank?.settings;
  return questionbankSettings?.dups_editable ?? false;
}

/**
 * Check Grade permissions
 */
function checkCanGrade(context: ExperienceAccessContextProps, userRole: string, isDraft: boolean) {
  const { embedded, currentUser, experience, enabledFeatures } = context;
  const isDupsEditable = getIsDupsEditable(experience);
  const isSessionsEnabled = shouldShowSessions(enabledFeatures, experience);
  // Disable grade tab in embedded mode
  if (embedded) {
    return {
      disabled: true,
      hidden: true,
      reason: 'ExperiencePermissions.grade_disabled_embedded_mode',
      label: 'ExperiencePageHeader.grade_tab',
    };
  }

  if (isDraft) {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.cannot_grade_draft_publish_first',
      label: 'ExperiencePageHeader.grade_tab',
    };
  }

  // Students cannot grade unless they're in peer-review mode
  // (canUserGrade → isStudentReviewMode handles the positive case below).
  if (userRole === 'student' && !isStudentReviewMode(experience, currentUser, enabledFeatures)) {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.students_cannot_grade',
      label: 'ExperiencePageHeader.grade_tab',
    };
  }

  if (userRole === 'parent') {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.parents_cannot_grade',
      label: 'ExperiencePageHeader.grade_tab',
    };
  }

  // Common assessment - enable grade tab for teachers/admins (if not draft)
  if (
    isSessionsEnabled &&
    (userRole === 'teacher' || userRole === 'admin') &&
    !isInvigilatorOnly(experience, currentUser)
  ) {
    return {
      disabled: false,
      hidden: false,
      reason: '',
      label: 'ExperiencePageHeader.grade_tab',
    };
  }

  if (userRole === 'qb_author' && currentUser?.org?.org_type === 'district') {
    if (isDupsEditable) {
      return {
        disabled: true,
        hidden: false,
        reason: 'ExperiencePermissions.no_permission_grade',
        label: 'ExperiencePageHeader.grade_tab',
      };
    }
    if (experience?.org_id === currentUser.org_id && !isDupsEditable) {
      return {
        disabled: false,
        hidden: false,
        reason: '',
        label: 'ExperiencePageHeader.grade_tab',
      };
    }
  }

  const canGrade = canUserGrade(context);

  if (!canGrade && !isOwnOrgExperience(experience, currentUser)) {
    return {
      disabled: false,
      hidden: true,
      reason: 'ExperiencePermissions.you_dont_have_access_to_grade_this_assessment',
      label: 'ExperiencePageHeader.grade_tab',
    };
  }

  return {
    disabled: !canGrade,
    hidden: false,
    reason: canGrade ? '' : 'ExperiencePermissions.no_permission_grade',
    label: 'ExperiencePageHeader.grade_tab',
  };
}

/**
 * Check Analytics permissions
 */
function checkCanAnalytics(
  context: ExperienceAccessContextProps,
  userRole: string,
  hasNoSubmissions: boolean,
  hasAnalyticsFeature: boolean,
  isValidRubricType: boolean,
  isDistrictQBAuthor: boolean
) {
  const { embedded, currentUser, experience, enabledFeatures } = context;
  const isDupsEditable = getIsDupsEditable(experience);
  const isSessionsEnabled = shouldShowSessions(enabledFeatures, experience);

  if (userRole === 'qb_author' && currentUser?.org?.org_type === 'district') {
    if (isDupsEditable) {
      return {
        disabled: true,
        hidden: false,
        reason: 'ExperiencePermissions.no_permission_analytics',
        label: 'ExperiencePageHeader.analyse_tab',
      };
    }
    if (experience?.org_id === currentUser.org_id && !isDupsEditable) {
      return {
        disabled: false,
        hidden: false,
        reason: '',
        label: 'ExperiencePageHeader.analyse_tab',
      };
    }
  }

  if (userRole === 'qb_author' && currentUser?.org?.org_type !== 'district') {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.no_permission_analytics',
      label: 'ExperiencePageHeader.analyse_tab',
    };
  }

  // Disable analytics tab in embedded mode
  if (embedded) {
    return {
      disabled: true,
      hidden: true,
      reason: 'ExperiencePermissions.analytics_disabled_embedded_mode',
      label: 'ExperiencePageHeader.analyse_tab',
    };
  }

  const canViewAnalytics = canUserEdit(context) || userRole === 'support';

  // Cross-org gate: hide before state-precondition branches so a cross-org user
  // doesn't see a "no submissions / feature off / invalid rubric" tooltip on an
  // assessment that isn't theirs. Sessions-enabled common assessments legitimately
  // span orgs, so teachers/admins on those keep falling through.
  const isSessionsCrossOrgAllowed =
    isSessionsEnabled &&
    (userRole === 'teacher' || userRole === 'admin') &&
    !isInvigilatorOnly(experience, currentUser);
  if (
    !isSessionsCrossOrgAllowed &&
    !canViewAnalytics &&
    !isOwnOrgExperience(experience, currentUser)
  ) {
    return {
      disabled: false,
      hidden: true,
      reason: 'ExperiencePermissions.you_dont_have_access_to_analyse_this_assessment',
      label: 'ExperiencePageHeader.analyse_tab',
    };
  }

  if (hasNoSubmissions && !isDistrictQBAuthor) {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.no_submissions_for_analysis',
      label: 'ExperiencePageHeader.analyse_tab',
    };
  }

  if (!hasAnalyticsFeature) {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.analytics_feature_not_enabled',
      label: 'ExperiencePageHeader.analyse_tab',
    };
  }

  if (!isValidRubricType) {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.analytics_not_available_comments_only',
      label: 'ExperiencePageHeader.analyse_tab',
    };
  }

  if (userRole === 'student') {
    return {
      disabled: true,
      hidden: false,
      reason: 'ExperiencePermissions.students_cannot_view_analytics',
      label: 'ExperiencePageHeader.analyse_tab',
    };
  }

  // Common assessment - enable analytics tab for teachers/admins
  if (
    isSessionsEnabled &&
    (userRole === 'teacher' || userRole === 'admin') &&
    !isInvigilatorOnly(experience, currentUser) &&
    experience?.submissions_count > 0
  ) {
    return {
      disabled: false,
      hidden: false,
      reason: '',
      label: 'ExperiencePageHeader.analyse_tab',
    };
  }

  return {
    disabled: !canViewAnalytics,
    hidden: false,
    reason: canViewAnalytics ? '' : 'ExperiencePermissions.no_permission_analytics',
    label: 'ExperiencePageHeader.analyse_tab',
  };
}

// Helper function to check if user has experience membership
function hasExperienceMembership(experience: any, currentUser: any): boolean {
  return !!experience?.experience_memberships_editors?.find(
    (em: any) => em.memberable_id === currentUser?.id
  );
}

// True when the assessment was opened from a room the current user invigilates.
// The BE sets `room_inviligator_membership` on experiences#show only when the
// `?room_uuid=` param resolves to a room this user is a verified invigilator of
// (see experiences_controller#pre_process_resource_before_render). It carries the
// user's room-invigilator membership(s); a non-empty value grants invigilator
// scope — Monitor, and Deliver once the test has started.
function hasRoomInvigilatorMembership(experience: any, currentUser: any): boolean {
  return !!experience?.room_inviligator_membership?.some(
    (m: any) => m.memberable_id === currentUser?.id
  );
}

// An invigilator's only route in is a monitor-only membership: no editor row, so the
// sessions tab short-circuits must not treat them as a full teacher.
//
// Teachers only. The invigilator picker accepts `by_role: 'teacher_admin'`, so an admin
// can hold an invigilator row too — but an admin always has an org-level route in
// (handleAdminPermissions grants can_edit, and every canUserX passes them via
// isAdminWithOrgAccess), so a proctor row must never narrow them.
//
// Known gap: a *session* author holds only an archived session-scoped owner row in
// practice, and `experience_memberships_editors` filters archived — so a session author
// who also invigilates is misclassified here. Needs a session-authorship signal to fix.
function isInvigilatorOnly(experience: any, currentUser: any): boolean {
  if (currentUser?.role !== 'teacher') return false;
  const isInvigilator =
    experience?.experience_membership_invigilator_ids?.includes(currentUser?.id) ||
    hasRoomInvigilatorMembership(experience, currentUser);
  return !!isInvigilator && !hasExperienceMembership(experience, currentUser);
}

// Helper function to check if user has experience membership or is an invigilator
function hasExpMembershipOrInvigilator(experience: any, currentUser: any): boolean {
  // Check if user is an experience editor with view permission
  if (hasExperienceMembership(experience, currentUser)) {
    return true;
  }

  // experience-level invigilator, or a room invigilator who opened the
  // assessment from their room — both can view once the test has started.
  const isInvigilator =
    experience?.experience_membership_invigilator_ids?.includes(currentUser?.id) ||
    hasRoomInvigilatorMembership(experience, currentUser);
  if (isInvigilator && checkIfStartTime(experience)) {
    return true;
  }

  return false;
}

// Helper function to check admin permissions for experience
function isAdminWithOrgAccess(experience: any, currentUser: any): boolean {
  return currentUser?.role === 'admin' && experience?.org_id === currentUser?.org_id;
}

// Helper function to check if it is an own org experience
function isOwnOrgExperience(experience: any, currentUser: any): boolean {
  const experienceOrgId = experience?.org_id;
  const userOrgId = currentUser?.org_id;
  if (experienceOrgId == null || userOrgId == null) return false;
  return String(experienceOrgId) === String(userOrgId);
}

function isStudentReviewMode(experience: any, currentUser: any, enabledFeatures: any): boolean {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const hasReviewerParam = urlParams.get('reviewer');
  return (
    currentUser?.role === 'student' &&
    hasReviewerParam &&
    canStudentReview(experience, enabledFeatures) &&
    experience.review_for_experience_users?.length > 0
  );
}

// Simplified permission helpers
function canUserEdit(context: ExperienceAccessContextProps): boolean {
  const { experience, currentUser } = context;

  // Admin can edit experiences in their org
  if (isAdminWithOrgAccess(experience, currentUser)) {
    return true;
  }
  // TODO: split this into 2 checks: 1. admin and 2. same org
  // TODO: add check if !same org, return false before checking experience membership - bug: case where same user is qb author and admin in school - able to edit in library because author id is same

  // Check if user is an experience editor
  if (
    hasExperienceMembership(experience, currentUser) &&
    currentUser?.permission?.experience?.update
  ) {
    return true;
  }

  return false;
}

function canUserView(context: ExperienceAccessContextProps): boolean {
  const { currentUser } = context;
  const userRole = currentUser?.role;

  if (canUserEdit(context)) return true;

  // Support and parent can view
  if (['support', 'parent'].includes(userRole)) {
    return true;
  }

  // Check if user has experience membership or is an invigilator
  if (hasExpMembershipOrInvigilator(context.experience, currentUser)) {
    return true;
  }

  return false;
}

function canUserDeliver(context: ExperienceAccessContextProps): boolean {
  const { experience, currentUser } = context;

  // Admin can deliver experiences in their org
  if (
    (isAdminWithOrgAccess(experience, currentUser) &&
      currentUser?.permission?.experience?.deliver) ||
    experience?.current_user_can_deliver
  ) {
    return true;
  }

  // Check experience membership permissions
  if (
    hasExperienceMembership(experience, currentUser) &&
    currentUser?.permission?.experience?.deliver
  ) {
    return true;
  }

  return false;
}

function canUserMonitor(context: ExperienceAccessContextProps): boolean {
  const { experience, currentUser, enabledFeatures } = context;

  // Admin can monitor experiences in their org
  if (
    isAdminWithOrgAccess(experience, currentUser) &&
    currentUser?.permission?.experience?.monitor
  ) {
    return true;
  }

  // Check experience membership permissions
  if (
    hasExperienceMembership(experience, currentUser) &&
    currentUser?.permission?.experience?.monitor
  ) {
    return true;
  }

  // Check invigilator permissions (experience-level, or a room invigilator who
  // opened the assessment from their room).
  const isInvigilator =
    experience?.experience_membership_invigilator_ids?.includes(currentUser?.id) ||
    hasRoomInvigilatorMembership(experience, currentUser);
  if (isInvigilator && currentUser?.permission?.experience?.monitor) {
    return true;
  }

  const isDistrictQBAuthorWithMonitorFlag = isDistrictQBAuthorWithMonitorFeature(
    enabledFeatures,
    currentUser
  );

  if (isDistrictQBAuthorWithMonitorFlag) {
    return true;
  }

  return false;
}

function canUserGrade(context: ExperienceAccessContextProps): boolean {
  const { experience, currentUser, enabledFeatures } = context;

  // Admin can grade experiences in their org
  if (isAdminWithOrgAccess(experience, currentUser) && currentUser?.permission?.experience?.grade) {
    return true;
  }

  // Check experience membership permissions
  if (
    hasExperienceMembership(experience, currentUser) &&
    currentUser?.permission?.experience?.grade
  ) {
    return true;
  }

  if (isStudentReviewMode(experience, currentUser, enabledFeatures)) {
    return true;
  }

  return false;
}
